'use client';

import { useAuth } from '@clerk/nextjs';
import { usePathname } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { SuperHelperAvatar } from '@/components/copilot/super-helper-avatar';
import { navbarHeaderPillClassName } from '@/components/layout/navbar-header-pill';
import { mobileShellTopBarTrailingClassName } from '@/components/layout/mobile-shell-top-bar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  isReachCopilotRouteEnabled,
  useOptionalReachCopilotShell,
} from '@/components/copilot/reach-copilot-context';
import { isSuperAdminViewClient } from '@/lib/permissions/client';

/** Mobile topbar trigger for Super Helper — replaces the floating FAB on small screens. */
export function ReachCopilotTopbarButton({
  className,
  embedded = false,
}: {
  className?: string;
  /** When true, parent supplies trailing layout (e.g. beside language globe). */
  embedded?: boolean;
}) {
  const pathname = usePathname() ?? '';
  const { isSignedIn, isLoaded, orgRole, sessionClaims } = useAuth();
  const shell = useOptionalReachCopilotShell();
  const { t } = useTranslation();

  const superAdmin = isSuperAdminViewClient(orgRole, sessionClaims);

  if (
    !isLoaded ||
    !isSignedIn ||
    !shell ||
    !isReachCopilotRouteEnabled(pathname, { allowAdminRoutes: superAdmin }) ||
    shell.suppressDock
  ) {
    return null;
  }

  const { dockOpen, setDockOpen } = shell;

  if (dockOpen) {
    return null;
  }

  const label = t('agent.reachCopilot.fabTitle', 'Open Super Helper');

  const trigger = (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            dir="ltr"
            className={cn(
              navbarHeaderPillClassName(false),
              'overflow-hidden rounded-lg',
            )}
            onClick={() => setDockOpen(true)}
            aria-label={t('agent.reachCopilot.fabAria', 'Open Super Helper')}
          >
            <SuperHelperAvatar className="h-8 w-8 rounded-lg" sizes="32px" />
          </button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  if (embedded) {
    return trigger;
  }

  return (
    <div className={cn('md:hidden', mobileShellTopBarTrailingClassName, className)}>
      {trigger}
    </div>
  );
}
